/**
 * Inline validation messages for the product buy form.
 *
 * Horizon leaves required fields to native browser validation. That works — Chrome shows
 * "Please select one of these options" — but the bubble is transient, generic, and anchors to
 * whichever control is invalid, which on the personalized products sits well below the fold
 * while the customer is clicking a sticky add-to-cart bar. The page lurches downward, a bubble
 * flashes for a few seconds, and it is gone.
 *
 * This replaces that with a persistent inline message next to the offending field, and one
 * smooth scroll to the first problem. Native `required` attributes stay exactly as they are, so
 * if this script fails to load the browser's own validation still runs as the fallback.
 *
 * Scoped to forms posting to /cart/add so collection-page quick-add and the contact/search
 * forms are left alone.
 */
(function () {
  'use strict';

  var ERROR_CLASS = 'jsps-field-error';
  var INVALID_CLASS = 'jsps-field-invalid';
  var styleInjected = false;

  function injectStyles() {
    if (styleInjected) return;
    styleInjected = true;
    var css = [
      '.' + ERROR_CLASS + '{',
      '  display:block;margin:.45rem 0 0;font-size:.8125rem;line-height:1.35;color:#A02B20;',
      '}',
      '.' + INVALID_CLASS + ' .jsps-design__opt span,',
      'input.' + INVALID_CLASS + ',textarea.' + INVALID_CLASS + '{',
      '  border-color:#A02B20;',
      '}'
    ].join('');
    var el = document.createElement('style');
    el.setAttribute('data-jsps-inline-validation', '');
    el.textContent = css;
    document.head.appendChild(el);
  }

  function isBuyForm(field) {
    var form = field.form;
    return !!(form && /\/cart\/add/.test(form.getAttribute('action') || ''));
  }

  /**
   * Where the message goes, and what to call the field.
   * Returns { anchor, container, label } or null if we cannot place a message safely.
   */
  function describe(field) {
    // Design chooser: radios inside the custom-liquid fieldset.
    var fieldset = field.closest('[data-jsps-design]');
    if (fieldset) {
      var legend = fieldset.querySelector('.jsps-design__label');
      return {
        anchor: fieldset.querySelector('.jsps-design__opts') || fieldset,
        container: fieldset,
        label: legend ? legend.textContent.trim() : 'design',
        isChoice: true
      };
    }

    // Horizon's product-custom-property block.
    var prop = field.closest('product-custom-property-component');
    if (prop) {
      var heading = prop.querySelector('.__heading');
      return {
        anchor: prop.querySelector('.__field') || field.parentElement,
        container: prop,
        label: heading ? heading.textContent.trim() : '',
        isChoice: false
      };
    }

    if (!field.parentElement) return null;
    var labelEl = field.id ? document.querySelector('label[for="' + CSS.escape(field.id) + '"]') : null;
    return {
      anchor: field,
      container: field.parentElement,
      label: labelEl ? labelEl.textContent.trim() : '',
      isChoice: field.type === 'radio' || field.type === 'checkbox'
    };
  }

  /**
   * Label-led phrasing, because the labels vary in shape: "Design" on the single-design
   * products but "First design" / "Folded design" on the bundles. Leading with the label and
   * an em dash reads correctly for all of them and avoids guessing at articles, while still
   * giving screen readers the field name — role="alert" announces the message alone.
   */
  function messageFor(info) {
    var label = (info.label || '').replace(/\s*\*\s*$/, '').trim();
    var tail = info.isChoice ? 'choose one to continue.' : 'fill this in to continue.';
    return label ? label + ' — ' + tail : tail.charAt(0).toUpperCase() + tail.slice(1);
  }

  function showError(field) {
    var info = describe(field);
    if (!info || !info.anchor || !info.container) return null;

    injectStyles();

    var existing = info.container.querySelector(':scope > .' + ERROR_CLASS);
    if (!existing) {
      existing = document.createElement('p');
      existing.className = ERROR_CLASS;
      existing.setAttribute('role', 'alert');
      if (info.anchor.parentElement === info.container) {
        info.anchor.insertAdjacentElement('afterend', existing);
      } else {
        info.container.appendChild(existing);
      }
    }
    existing.textContent = messageFor(info);

    info.container.classList.add(INVALID_CLASS);
    if (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA') {
      if (field.type !== 'radio' && field.type !== 'checkbox') field.classList.add(INVALID_CLASS);
    }
    field.setAttribute('aria-invalid', 'true');

    return info.container;
  }

  function clearError(container) {
    if (!container) return;
    var msg = container.querySelector(':scope > .' + ERROR_CLASS);
    if (msg) msg.remove();
    container.classList.remove(INVALID_CLASS);
    container.querySelectorAll('.' + INVALID_CLASS).forEach(function (el) {
      el.classList.remove(INVALID_CLASS);
    });
    container.querySelectorAll('[aria-invalid="true"]').forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
  }

  var pending = [];
  var scheduled = false;

  function flush() {
    scheduled = false;
    var first = pending[0];
    pending = [];
    if (!first) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      first.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    } catch (e) {
      first.scrollIntoView();
    }
  }

  // `invalid` does not bubble, so listen in the capture phase.
  document.addEventListener(
    'invalid',
    function (event) {
      var field = event.target;
      if (!field || !field.form || !isBuyForm(field)) return;

      // Suppress the native bubble in favour of the inline message.
      event.preventDefault();

      var container = showError(field);
      if (container && pending.indexOf(container) === -1) pending.push(container);

      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    },
    true
  );

  // Clear a field's message as soon as the customer fixes it.
  function handleFix(event) {
    var field = event.target;
    if (!field || !field.form || !isBuyForm(field)) return;
    var info = describe(field);
    if (!info) return;
    if (field.checkValidity()) clearError(info.container);
  }

  document.addEventListener('change', handleFix, true);
  document.addEventListener('input', handleFix, true);
})();
